import { Component, OnInit } from '@angular/core';
import { FuncionarioService } from '@services/funcionario.service';
import { FuncionarioEvaluado } from '@models/funcionario.interface';
import { DocumentoService } from '@services/documento.service';
import { SessionService } from '@services/session.service';
import { AlertService } from '@services/alert.service';

@Component({
  selector: 'app-presentacion-inicial',
  templateUrl: './presentacion-inicial.component.html',
  styleUrls: ['./presentacion-inicial.component.css']
})
export class PresentacionInicialComponent implements OnInit {
  public funcionario: FuncionarioEvaluado | null = null;
  
  constructor(
    private funcionarioService: FuncionarioService,
    private documentoService: DocumentoService,
    private sessionService: SessionService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.cargarFuncionario();
  }

  async cargarFuncionario() {
    try {
      const sesion = this.sessionService.getSesionActual();
      if (!sesion || !sesion.rut_usuario) {
        this.alertService.error('No hay sesión activa o el RUT no está disponible.');
        return;
      }
      const rut = sesion.rut_usuario.replace(/\D/g, ''); // Extract numeric part
      this.funcionario = await this.funcionarioService.obtenerFuncionario(rut);
      console.log('Funcionario cargado:', this.funcionario);
    } catch (error) {
      console.error('Error al cargar funcionario:', error);
      this.alertService.error('No se pudo cargar la información del funcionario.');
    }
  }
  public selectedFiles: File[] = [];
  public maxFiles = 10;
  public maxFileSize = 20 * 1024 * 1024; // 20MB
  public accept = '.pdf,.zip';
  public isDragging = false;
  public uploadProgress = 0;
  public isUploading = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.addFiles(Array.from(files));
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(Array.from(input.files));
      input.value = ''; // Reset input
    }
  }

  private addFiles(files: File[]): void {
    const validFiles = files.filter(f => this.isValidFile(f));
    
    // Check if adding these files would exceed max
    const remainingSlots = this.maxFiles - this.selectedFiles.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    if (filesToAdd.length < validFiles.length) {
      this.alertService.warning(`Solo se pueden agregar ${remainingSlots} archivos más. Máximo ${this.maxFiles} archivos.`);
    }

    this.selectedFiles.push(...filesToAdd);
  }

  private isValidFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    const isPdf = fileName.endsWith('.pdf');
    const isZip = fileName.endsWith('.zip');
    
    if (!isPdf && !isZip) {
      this.alertService.error(`El archivo "${file.name}" no es válido. Solo se aceptan archivos PDF o ZIP.`);
      return false;
    }

    if (file.size > this.maxFileSize) {
      this.alertService.error(`El archivo "${file.name}" excede el tamaño máximo de 20MB.`);
      return false;
    }

    return true;
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  clearAll(): void {
    this.selectedFiles = [];
  }

  getFileIcon(fileName: string): string {
    return fileName.toLowerCase().endsWith('.pdf') ? 'bi-file-pdf' : 'bi-file-zip';
  }

  getFileIconColor(fileName: string): string {
    return fileName.toLowerCase().endsWith('.pdf') ? '#e74c3c' : '#f39c12';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  async onSubmit() {
    if (this.selectedFiles.length === 0) {
      this.alertService.warning('Por favor, seleccione al menos un archivo para subir.');
      return;
    }

    if (!this.funcionario) {
        this.alertService.warning('No se ha cargado la información del funcionario.');
        return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    try {
        const sesion = this.sessionService.getSesionActual();
        const rutUsuario = sesion ? parseInt(sesion.rut_usuario.replace(/\D/g, ''), 10) : 1; 

        let uploadedCount = 0;
        const totalFiles = this.selectedFiles.length;
        const progressPerFile = 100 / totalFiles;

        for (const file of this.selectedFiles) {
            const base64Full = await this.toBase64(file);
            const base64 = base64Full.split(',')[1]; 

            const payload = {
                exp_id: this.funcionario.exp_id,
                usu_rut: rutUsuario,
                tdo_codigo: 1, // Presentacion Inicial
                doc_nombre_archivo: file.name,
                archivo: base64
            };
            
            await this.documentoService.subirDocumento(payload);
            
            uploadedCount++;
            this.uploadProgress = Math.round(uploadedCount * progressPerFile);
        }

        this.uploadProgress = 100;
        this.alertService.success(`Se han cargado ${this.selectedFiles.length} archivo(s) exitosamente.`);
        this.selectedFiles = [];
    } catch (error: any) {
        console.error('Error uploading files:', error);
        let msg = 'Error al subir los archivos. Por favor intente nuevamente.';
        if (error.error && error.error.data && error.error.data.error) {
            msg = error.error.data.error;
        }
        this.alertService.error(msg);
    } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
    }
  }

  private toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });
  }
}
